import type { StockStatus } from '@jouzen/ecom-components/v3/types/stockStatus';
import { useRouter } from 'next/navigation';
import { useLocale } from 'next-intl';
import { useCallback } from 'react';

import useProductAnalytics from '@/analytics/hooks/useProductAnalytics';
import { EventType } from '@/analytics/types';
import { WIZARD_PAGE_VIEWED } from '@/app/[locale]/store/_consts';
import { STORE_ACCESSORIES_ROOT_PATH } from '@/app/consts/pathnames';
import logToDatadog from '@/logger/logToDatadog';
import { addToCart } from '@/queries/AddToCart';
import { changeWarranty } from '@/queries/ChangeWarranty';
import { removeFromCart } from '@/queries/RemoveFromCart';
import { getCartIdCookie } from '@/utils/cartUtils';
import { getCookie } from '@/utils/cookie';

import sendBundleAnalytics from '../_analytics/sendBundleAnalytics';
import MembershipSelection from '../_steps/enums/MembershipSelection';
import type { WarrantyObject } from '../_utils/buildWarrantyObject';
import buildWarrantyObject from '../_utils/buildWarrantyObject';
import getExtendedWarrantySku from '../_utils/getExtendedWarrantySku';
import type { MembershipObject } from './types';
import type { UseWizardState } from './useWizardState/types';

interface AddProductToCartProps {
  variantId: { sku: string };
  isAccessory: boolean | undefined;
  membershipObj: MembershipObject;
  onAddToCartSuccess: () => Promise<void>;
  ringCount: number | undefined;
  warrantySku: string;
}

const addProductToCart = async ({
  variantId,
  isAccessory,
  membershipObj,
  onAddToCartSuccess,
  ringCount,
  warrantySku,
}: AddProductToCartProps): Promise<void> => {
  try {
    const onSuccess = () => {
      // When cart 2.0 is enabled, use this as the trigger to advance to the next page.
      void onAddToCartSuccess();
    };

    // Add item to the Cart 2.0 cart.
    await addToCart(variantId.sku, warrantySku);
    if (membershipObj.membershipSelection === MembershipSelection.OneYear) {
      await addToCart(membershipObj.sku, warrantySku);
    } else {
      await removeFromCart(membershipObj.sku, null);
    }

    // TODO: ECOM-5460 move this logic to the backend, and remove from here
    const shouldChangeWarranty = (ringCount ?? 0) > 0 && !isAccessory;
    // update warranties when this is not the first ring
    if (shouldChangeWarranty) {
      try {
        await changeWarranty(warrantySku);
        onSuccess();
      } catch (error) {
        logToDatadog({
          data: { warrantySku, ringCount },
          error,
          message: 'cart 2.0 changeWarranty failed after addToCart',
          module: 'cart',
        });
      }
    } else {
      onSuccess();
    }
  } catch (error) {
    logToDatadog({
      error,
      message: 'Error thrown during addToCart (cart 2.0)',
      module: 'cart',
    });
  }
};

interface UseAddToCartProps {
  stockStatus: StockStatus;
}

const useAddToCart = ({
  currentRoute,
  isAccessory,
  membershipSelection,
  oneYearMembership,
  product,
  ringCount,
  stockStatus,
  threeYearWarranty,
  twoYearWarranty,
  warrantySelection,
  setIsAddingToCart,
}: UseWizardState & UseAddToCartProps) => {
  const router = useRouter();
  const locale = useLocale();
  const { sendProductAddedEvent } = useProductAnalytics();

  const baseAccessoryPath = STORE_ACCESSORIES_ROOT_PATH;
  const style = currentRoute.style;
  const finish = currentRoute.finish;
  const size = currentRoute.size;
  const sizeValue = size.value;

  const onAddToCartSuccess = useCallback(async () => {
    await sendProductAddedEvent({
      path: `product/rings/${style.value}/${finish.value}/${sizeValue}`,
      product,
      stockStatus,
    });

    localStorage.removeItem(WIZARD_PAGE_VIEWED);
    router.push(`/${locale}/cart`);
  }, [locale, router]); // eslint-disable-line react-hooks/exhaustive-deps -- fix in follow-up

  const handleAddToCart = useCallback(async () => {
    setIsAddingToCart(true);

    const variants = product?.variants;
    const productsVariants = oneYearMembership?.variants;

    const cartId = getCartIdCookie();

    const membershipSku: string = Array.isArray(productsVariants)
      ? (productsVariants[0]?.sku ?? '')
      : '';

    const warrantySku = getExtendedWarrantySku(warrantySelection) ?? '';
    const fbc = getCookie('_fbc');
    const fbp = getCookie('_fbp');
    const image_url = product?.images?.[0]?.originalSrc;
    const url = `https://www.ouraring.com/oura-gen3/${style.value}/${finish.value}/${sizeValue}`;

    const membershipObj: MembershipObject = {
      currency: oneYearMembership?.price?.currencyCode ?? 'USD',
      fbc,
      fbp,
      image_url,
      membershipSelection,
      price: oneYearMembership?.price?.amount,
      product_id: oneYearMembership?.id,
      sku: membershipSku,
      url,
      variant: oneYearMembership?.variants[0]?.productId,
    };

    const warrantyObj: WarrantyObject = buildWarrantyObject({
      fbc,
      fbp,
      image_url,
      url,
      twoYearWarranty,
      threeYearWarranty,
      warrantySelection,
    });

    if (!isAccessory) {
      void sendBundleAnalytics({
        cart_id: cartId,
        membershipObj,
        warrantyObj,
      });
    } else {
      // Add to cart button clicked for accessories
      await window.ouraAnalytics.track(EventType.CTAClicked, {
        action: 'cart',
        cta: 'add_to_cart',
        location: 'wizard',
        path: `${baseAccessoryPath}/${currentRoute.accessory.value}/${sizeValue}`,
      });
    }

    // this checks the size value and returns either a value or mapped value to get the variant
    const selectOptions = sizeValue === 'sizing-kit' ? 'Size later' : sizeValue;
    const variantId = variants?.find((v) => {
      if (Array.isArray(v.selectedOptions) && v.selectedOptions.length > 0) {
        return (
          v.selectedOptions.find((o) => o?.name === 'Size')?.value ===
          selectOptions
        );
      }
      return false;
    });

    if (!variantId || !membershipObj.sku) return;

    void addProductToCart({
      variantId,
      isAccessory,
      membershipObj,
      onAddToCartSuccess,
      ringCount,
      warrantySku,
    });
  }, [
    setIsAddingToCart,
    product?.variants,
    product?.images,
    oneYearMembership?.variants,
    oneYearMembership?.price?.currencyCode,
    oneYearMembership?.price?.amount,
    oneYearMembership?.id,
    warrantySelection,
    style.value,
    finish.value,
    sizeValue,
    membershipSelection,
    twoYearWarranty,
    threeYearWarranty,
    isAccessory,
    onAddToCartSuccess,
    ringCount,
    baseAccessoryPath,
    currentRoute.accessory.value,
  ]);

  return { onAddToCart: handleAddToCart };
};

export default useAddToCart;
